import React, { useState, useContext, useEffect, useMemo } from 'react';

import { TextInput, FileUpload } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import {
  allCargoCompanies,
  allTransportStatus,
  DomesticTrackingDetailItem,
  TrackingDataDeleteQuery,
  TrackingDetailItem,
  TransportStatus,
} from '@models/TrackingData';
import { makeMuiStyles, useCommonStyles } from '@styles';
import {
  formStrings,
  createValidate,
  requiredStringField,
  stringField,
  remoteFileField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatInputDate, formatInputTime } from '@utils/types';
import { Formik, Form, useFormikContext } from 'formik';
import { fold } from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    addTransportStatus: '輸送状況を追加する',
    editTransportStatus: '輸送状況を編集する',
    d2dNo: 'D2D番号',
    date: '日付',
    time: '時刻',
    note: '備考',
    document: '書類',
    updateDocument: '書類アップロード',
    transportStatus: '運送状況',
    coopName: '運送方法',
    carNo: '車両番号',
    carDriver: 'ドライバー名',
    coopTrackingNo: '追跡番号',
    confirmDelete: '本当に削除しますか？',
    driverInfo: 'ドライバー情報',
    uploadDeliveryInvoice: '送り状アップロード',
  },
});

const transportMethodJaDictionary = {
  'Charter Trucks': 'チャータートラック',
  Container: 'コンテナ',
  'Consolidation Company': '混載会社',
} as const;

const transportMethodStrings = createStrings({ ja: transportMethodJaDictionary });

const transportStatusOptions = allTransportStatus
  .filter(
    (v) =>
      ![
        'キャンセル済',
        'ラベル生成',
        '通関と納品の最終指示',
        'Sea Waybill発行',
        'Arrival Notice生成',
        '請求書発行',
        '国内配達中',
      ].includes(v),
  )
  .map((value) => ({ label: value, value }));
// eslint-disable-next-line no-underscore-dangle
const transportMethodOptions = (
  transportMethodStrings as unknown as {
    _defaultLanguageFirstLevelKeys: (keyof typeof transportMethodStrings)[];
  }
)._defaultLanguageFirstLevelKeys.map((key) => {
  const option = (labelAndValue: string) => {
    return { label: labelAndValue, value: labelAndValue };
  };

  const label = transportMethodJaDictionary[key as keyof typeof transportMethodJaDictionary];

  switch (key) {
    case 'Charter Trucks': {
      return {
        label,
        children: [option(transportMethodStrings[key])],
      };
    }
    case 'Container': {
      return {
        label,
        children: [option(transportMethodStrings[key])],
      };
    }
    case 'Consolidation Company': {
      return {
        label,
        children: allCargoCompanies.map(option),
      };
    }
    default: {
      throw new Error(`unknown key: ${key}`);
    }
  }
});

const initialUIState = {
  transportStatus: '',
  coopName: '',
};

const uiStateContext = React.createContext<{
  UIState: typeof initialUIState;
  setUIState: React.Dispatch<React.SetStateAction<typeof initialUIState>>;
}>({} as any);

const useStyles = makeMuiStyles((_theme, _common) => ({
  rootDialog: {
    zIndex: `1301 !important` as any,
  },
}));

const TransportStatusFormWithCharterTruckOrContainer: React.FC = () => {
  return (
    <>
      <TextInput name="carNo" label={strings.carNo} />
      <TextInput name="carDriver" label={strings.carDriver} />
    </>
  );
};

const TransportStatusFormWithDriverInfo: React.FC = () => {
  return (
    <>
      <TextInput name="note" multiline label={strings.driverInfo} />
    </>
  );
};

const TransportStatusFormWithDeliveryInvoice: React.FC = () => {
  return (
    <>
      <FileUpload
        name="file"
        label={strings.document}
        buttonText={strings.updateDocument}
        confirmStatus={false}
      />
    </>
  );
};

const TransportStatusFormWithConsolidationCompany: React.FC = () => {
  return (
    <>
      <TextInput name="coopTrackingNo" label={strings.coopTrackingNo} />
    </>
  );
};

const TransportStatusFormWithDocumentUploader: React.FC = () => {
  return (
    <>
      <TextInput name="note" multiline label={strings.note} />
      <FileUpload
        name="file"
        label={strings.document}
        buttonText={strings.updateDocument}
        confirmStatus={false}
      />
    </>
  );
};

const TransportStatusFormWithDomesticDelivery: React.FC = () => {
  const { setUIState } = useContext(uiStateContext);
  const { values } = useFormikContext<{ coopName: string }>();

  useEffect(() => {
    if (values.coopName !== '') {
      setUIState((state) => {
        return {
          ...state,
          coopName: values.coopName,
        };
      });
    }
  }, [values, setUIState]);

  return (
    <>
      <TextInput name="coopName" label={strings.coopName} choose options={transportMethodOptions} />
    </>
  );
};

interface TransportStatusFormProps {
  type: 'edit' | 'add';
}

const TransportStatusForm: React.FC<TransportStatusFormProps> = (props) => {
  const { type } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [UIState, setUIState] = useState(() => initialUIState);
  const { values } = useFormikContext<{ transportStatus: string }>();

  useEffect(() => {
    if (values.transportStatus !== '') {
      setUIState((state) => {
        return {
          ...state,
          transportStatus: values.transportStatus,
        };
      });
    }
  }, [values, setUIState]);

  return (
    <uiStateContext.Provider value={{ UIState, setUIState }}>
      <div className={classes.verticalSpace1}>
        <TextInput name="d2dNo" label={strings.d2dNo} disabled />
        <TextInput name="date" type="date" label={strings.date} />
        <TextInput name="time" type="time" label={strings.time} />
        <TextInput
          name="transportStatus"
          label={strings.transportStatus}
          choose
          options={transportStatusOptions}
          disabled={type === 'edit'}
        />

        {['国内配達中', 'ドライバー情報・送り状'].includes(UIState.transportStatus) ? (
          <TransportStatusFormWithDomesticDelivery />
        ) : (
          UIState.transportStatus != null &&
          UIState.transportStatus !== '' && <TransportStatusFormWithDocumentUploader />
        )}

        {UIState.transportStatus === '国内配達中' &&
          ['チャータートラック', 'コンテナ'].includes(UIState.coopName) && (
            <TransportStatusFormWithCharterTruckOrContainer />
          )}

        {UIState.transportStatus === '国内配達中' &&
          !['チャータートラック', 'コンテナ'].includes(UIState.coopName) &&
          UIState.coopName !== null &&
          UIState.coopName !== '' && <TransportStatusFormWithConsolidationCompany />}

        {UIState.transportStatus === 'ドライバー情報・送り状' &&
          ['チャータートラック', 'コンテナ'].includes(UIState.coopName) && (
            <TransportStatusFormWithDriverInfo />
          )}

        {UIState.transportStatus === 'ドライバー情報・送り状' &&
          !['チャータートラック', 'コンテナ'].includes(UIState.coopName) &&
          UIState.coopName !== null &&
          UIState.coopName !== '' && <TransportStatusFormWithDeliveryInvoice />}
      </div>
    </uiStateContext.Provider>
  );
};

export const AddTransportStatusDialog: React.FC<AddTransportStatusDialogProps> = (props) => {
  const {
    open,
    type,
    d2dNo,
    transportStatus,
    editingItem: initialValues,
    onClose,
    onSubmit,
    onDelete,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const coopNameType = useMemo(
    () =>
      new t.Type<string, string, string | undefined>(
        'coopName',
        (input: any): input is string => {
          return typeof input === 'string';
        },
        (input, context) => {
          if (
            (context[0] as { actual: AddTransportStatusFormValues }).actual.transportStatus ===
            '国内配達中'
          ) {
            const result = requiredStringField(strings.coopName).decode(input ?? '');

            return pipe(
              result,
              fold(
                (errors) => {
                  return t.failure(input, context, errors[0].message);
                },
                () => t.success(input ?? ''),
              ),
            );
          }

          return t.success(input ?? '');
        },
        t.identity,
      ),
    [],
  );

  const schema = useMemo(() => {
    return t.type({
      d2dNo: requiredStringField(strings.d2dNo),
      date: requiredStringField(strings.date),
      time: requiredStringField(strings.time),
      transportStatus: requiredStringField(strings.transportStatus),
      coopName: coopNameType as t.Mixed,
      note: stringField(strings.note),
      file: h.omittable(h.array(remoteFileField(strings.document, true))),
    });
  }, []);

  const validate = useMemo(() => createValidate(schema), [schema]);

  const renderConfirmDeleteForm = () => (
    <ActionableDialog
      classes={{ root: classes.rootDialog }}
      open={confirmDeleteOpen}
      onClose={() => setConfirmDeleteOpen(false)}
    >
      <ActionableDialogTitle>{formStrings.confirm}</ActionableDialogTitle>

      <ActionableDialogContent>{strings.confirmDelete}</ActionableDialogContent>

      <ActionableDialogActions>
        <Button
          color="warning"
          size="small"
          text={formStrings.yes}
          type="button"
          onClick={async () => {
            setConfirmDeleteOpen(false);
            try {
              await onDelete?.({ key: initialValues?.key!, transport_status: transportStatus! });
            } finally {
              onClose();
            }
          }}
        />
        <Button
          color="primary"
          size="small"
          text={formStrings.cancel}
          type="button"
          onClick={() => setConfirmDeleteOpen(false)}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );

  return (
    <>
      {renderConfirmDeleteForm()}
      <Formik<t.TypeOf<typeof schema>>
        validate={validate}
        initialValues={{
          d2dNo,
          date: type === 'edit' ? formatInputDate(new Date(initialValues?.time!)) ?? '' : '',
          time: type === 'edit' ? formatInputTime(new Date(initialValues?.time!)) ?? '' : '',
          transportStatus: type === 'edit' ? transportStatus ?? '' : '',
          coopName:
            type === 'edit' ? (initialValues as DomesticTrackingDetailItem).coop_name ?? '' : '',
          note: type === 'edit' ? initialValues?.note ?? '' : ('' as TransportStatus),
          file: undefined,
        }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        <Form>
          <ActionableDialog open={open} onClose={onClose}>
            <ActionableDialogTitle>
              {type === 'add' && strings.addTransportStatus}
              {type === 'edit' && strings.editTransportStatus}
            </ActionableDialogTitle>
            <ActionableDialogContent className={classes.overflowVisible}>
              <TransportStatusForm type={type} />
            </ActionableDialogContent>
            <ActionableDialogActions justifyContent={'between'}>
              {type === 'edit' && (
                <Button
                  color="grey"
                  size="small"
                  text={formStrings.delete}
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                />
              )}
              <Button
                color="primary"
                size="small"
                text={type === 'add' ? formStrings.create : formStrings.update}
                type="submit"
              />
            </ActionableDialogActions>
          </ActionableDialog>
        </Form>
      </Formik>
    </>
  );
};

export interface AddTransportStatusFormValues {
  d2dNo: string;
  date: string;
  time: string;
  transportStatus: string;
  coopName?: string;
  carNo?: string;
  coopTrackingNo?: string;
  note?: string;
  file?:
    | {
        file: File | null | undefined;
        remote: string | null | undefined;
      }[]
    | null;
  carDriver?: string;
}
export interface AddTransportStatusDialogProps {
  open: boolean;
  d2dNo: string;
  transportStatus?: TransportStatus;
  editingItem?: DomesticTrackingDetailItem | TrackingDetailItem;
  type: 'edit' | 'add';
  onClose: () => any;
  onSubmit: (values: AddTransportStatusFormValues) => any;
  onDelete?: (query: TrackingDataDeleteQuery) => Promise<any>;
}
